// src/containers/pages/caseManagement/hooks/useCaseDeletability.ts

import { useCallback, useRef, useState } from 'react';
import getCaseDeletability from 'api/cases/getCaseDeletability';
import deleteCaseGuarded from 'api/cases/deleteCaseGuarded';
import { CaseDeletabilityData } from 'api/cases/types';
import { useNotificationProvider } from 'hooks';
import { getServerErrorMessage } from '../utils/deleteButtonState';

export interface UseCaseDeletability {
  deletability: CaseDeletabilityData | null;
  loadingDeletability: boolean;
  deleting: boolean;
  refreshDeletability: (
    caseId: number,
    opts?: { silent?: boolean },
  ) => Promise<void>;
  clearDeletability: () => void;
  executeDelete: (
    caseId: number,
    confirmedAttachments: boolean,
  ) => Promise<boolean>;
}

/**
 * Deletability state + guarded delete for the Source Case (design §6).
 * Composed by useCaseManagement - never used by the dashboard directly.
 */
const useCaseDeletability = (onDeleted: () => void): UseCaseDeletability => {
  const [deletability, setDeletability] = useState<CaseDeletabilityData | null>(
    null,
  );
  const [loadingDeletability, setLoadingDeletability] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { notifySuccess, notifyError, notifyWarning } =
    useNotificationProvider();
  // Stale-response guard (FE-D10): only the LATEST request may write state -
  // rapid source switching must never leave case A's data rendered while the
  // delete targets case B.
  const requestSeq = useRef(0);

  const refreshDeletability = useCallback(
    async (caseId: number, opts?: { silent?: boolean }) => {
      const seq = ++requestSeq.current;
      setLoadingDeletability(true);
      try {
        const data = await getCaseDeletability(caseId);
        if (seq !== requestSeq.current) return; // stale - a newer request owns the state
        setDeletability(data);
      } catch (error) {
        if (seq !== requestSeq.current) return;
        // background check failure => warning tier
        // (FRONTEND_ERROR_HANDLING_STANDARDS), button hides (FE-D6)
        console.error(
          '[useCaseDeletability] Failed to refresh deletability:',
          error,
        );
        setDeletability(null);
        // silent after a failed delete - the server's reason toast must survive (FE-V8)
        if (!opts?.silent) {
          notifyWarning(
            'Could not determine whether this case can be deleted.',
          );
        }
      } finally {
        if (seq === requestSeq.current) setLoadingDeletability(false);
      }
    },
    [notifyWarning],
  );

  const clearDeletability = useCallback(() => {
    requestSeq.current += 1; // invalidate anything in flight
    setDeletability(null);
    setLoadingDeletability(false);
  }, []);

  const executeDelete = useCallback(
    async (caseId: number, confirmedAttachments: boolean): Promise<boolean> => {
      setDeleting(true);
      try {
        const result = await deleteCaseGuarded(caseId, confirmedAttachments);
        const residueNote =
          result.residueSweep && !result.residueSweep.clean
            ? ' Residue sweep reported leftovers - check the delete log.'
            : '';
        notifySuccess(`Case #${caseId} deleted.${residueNote}`);
        clearDeletability();
        onDeleted();
        return true;
      } catch (error) {
        console.error('[useCaseDeletability] Delete failed:', error);
        notifyError(
          getServerErrorMessage(
            error,
            'Failed to delete case. Please try again.',
          ),
        );
        // design §6: button re-fetches after any failed delete
        await refreshDeletability(caseId, { silent: true });
        return false;
      } finally {
        setDeleting(false);
      }
    },
    [
      notifySuccess,
      notifyError,
      refreshDeletability,
      clearDeletability,
      onDeleted,
    ],
  );

  return {
    deletability,
    loadingDeletability,
    deleting,
    refreshDeletability,
    clearDeletability,
    executeDelete,
  };
};

export default useCaseDeletability;
